import { format } from "date-fns";
import { createWebsocketSession, WebsocketSession } from "./websocketSession";
import { ServerWebsockEvent } from "../websocketEventTypes";

export type DebugLog = (...args: unknown[]) => void;

const logTimestamp = (): string => format(new Date(), "yyMMdd HH:mm:ss");

// Capped exponential backoff, retried indefinitely so a long outage does't strand a client
export const RECONNECT_BASE_DELAY_MS = 1000;
export const RECONNECT_MAX_DELAY_MS = 120000;

export const nextReconnectDelayMs = (attempt: number): number =>
  Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS);

export enum ReadyState {
  UNINSTANTIATED = -1,
  CONNECTING = 0,
  OPEN = 1,
  CLOSING = 2,
  CLOSED = 3,
}

export type WebsocketMessage = {
  eventType: string;
};

// Satisfied structurally by both a browser's WebSocket and React Native's — no adapter needed.
// Only `connect` (below) is platform-specific, since auth differs: browsers attach the cookie
// automatically, React Native needs it passed as a header at construction.
export type MinimalSocket = {
  readyState: number;
  send: (data: string) => void;
  close: () => void;
  onopen: (() => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code?: number; reason?: string } | undefined) => void) | null;
  onerror: ((event: { message?: string } | undefined) => void) | null;
};

export type WebsocketClientOptions = {
  wsUrl: string;
  organizationId: number;
  // Builds a socket for one attempt against url (wsUrl + organizationId as a query param).
  // Return null to skip this attempt (e.g. auth cookie momentarily unavailable) — retries on
  // the normal backoff.
  connect: (url: string) => MinimalSocket | null;
  pingIntervalMs: number;
  onReadyStateChange: (state: ReadyState) => void;
  onMessage: (message: WebsocketMessage) => void;
  onConnectionId: (connectionId: string | undefined) => void;
  // A function so it can be toggled live
  isDebugEnabled?: () => boolean;
};

export type WebsocketClient = {
  start: () => void;
  stop: () => void;
  sendJsonMessage: (message: unknown) => boolean;
};

// What each platform's context exposes to app code. Distinct from WebsocketClient above — the
// internal driver a provider's effect owns — since app code shouldn't call start/stop.
export type WebsocketSocket = {
  readyState: ReadyState;
  lastJsonMessage: WebsocketMessage | null;
  sendJsonMessage: (message: unknown) => boolean;
};

// Owns everything about keeping a connection alive that isn't platform-specific
// and surfaces readyState/message/connectionId changes via callbacks
export const createWebsocketClient = (options: WebsocketClientOptions): WebsocketClient => {
  const isDebugEnabled = options.isDebugEnabled ?? (() => false);
  const debugLog: DebugLog = (...args) => {
    if (isDebugEnabled()) {
      console.info(logTimestamp(), ...args);
    }
  };
  const url = `${options.wsUrl}?organizationId=${options.organizationId}`;
  let cancelled = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  let reconnectAttempts = 0;
  let socket: MinimalSocket | null = null;
  let session: WebsocketSession | null = null;

  const rawSend = (message: unknown): boolean => {
    if (!socket || socket.readyState !== ReadyState.OPEN) {
      return false;
    }
    socket.send(JSON.stringify(message));
    return true;
  };

  const sendJsonMessage = (message: unknown): boolean => {
    const sent = rawSend(message);
    if (sent) {
      debugLog("WS outgoing:", (message as { eventType?: string })?.eventType || "", JSON.stringify(message));
    } else {
      debugLog("WS send skipped: socket not open");
    }
    return sent;
  };

  const scheduleReconnect = (): void => {
    if (cancelled) {
      return;
    }
    const delay = nextReconnectDelayMs(reconnectAttempts);
    reconnectAttempts += 1;
    reconnectTimer = setTimeout(attemptConnect, delay);
  };

  const attemptConnect = (): void => {
    if (cancelled) {
      return;
    }
    debugLog("WS connecting:", url);
    const newSocket = options.connect(url);
    if (!newSocket) {
      scheduleReconnect();
      return;
    }
    options.onReadyStateChange(ReadyState.CONNECTING);
    socket = newSocket;

    const newSession = createWebsocketSession(options.pingIntervalMs, {
      sendJsonMessage: rawSend,
      onRecycleConnection: () => {
        debugLog("WS recycling connection");
        newSocket.close();
      },
      onConnectionId: options.onConnectionId,
      debugLog,
    });
    session = newSession;

    newSocket.onopen = () => {
      if (cancelled) {
        return;
      }
      reconnectAttempts = 0;
      options.onReadyStateChange(ReadyState.OPEN);
      debugLog("WS connection status: Open");
      newSession.start();
    };

    newSocket.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data as string) as ServerWebsockEvent;
        options.onMessage(parsed);
        if (parsed) {
          newSession.handleMessage(parsed);
        }
      } catch {
        console.error("WS received non-JSON message");
      }
    };

    newSocket.onerror = (event) => {
      // Browsers give little detail here; RN's polyfill adds `message`. onclose follows either
      // way and drives the actual reconnect.
      console.error("WS connection status: Error", event?.message ?? "(no message)");
    };

    newSocket.onclose = (event) => {
      if (socket === newSocket) {
        socket = null;
      }
      newSession.stop();
      if (session === newSession) {
        session = null;
      }
      options.onReadyStateChange(ReadyState.CLOSED);
      if (cancelled) {
        return;
      }
      debugLog("WS connection status: Closed", `code=${event?.code ?? "?"}`, event?.reason ? `reason=${event.reason}` : "");
      scheduleReconnect();
    };
  };

  return {
    start: attemptConnect,
    stop: () => {
      cancelled = true;
      if (reconnectTimer !== undefined) {
        clearTimeout(reconnectTimer);
        reconnectTimer = undefined;
      }
      session?.stop();
      session = null;
      socket?.close();
      socket = null;
    },
    sendJsonMessage,
  };
};
